import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import {
  Clock,
  Play,
  Pause,
  Download,
  Undo2,
  Redo2,
  RotateCcw,
  Pencil,
  Loader2,
  AlertTriangle,
  Type,
  ChevronUp,
  ChevronDown,
  ChevronsUp,
  Wand2,
  SlidersHorizontal,
  HelpCircle,
  CornerDownLeft,
  ShieldCheck,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import ConfirmDialog from '../ui/ConfirmDialog';
import {
  parseSrt,
  normalizeWords,
  alignSrtToWords,
  blocksFromBreaks,
  buildSrt,
  formatSrtTime,
  wordsUrlFromSrtUrl,
  smartBreaksRanged,
} from '../../utils/srtEditor';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

/**
 * SrtEditorModal — Editor do SRT Tempo de um áudio gerado.
 *
 * Carrega o srtsynctempo.srt + words_time.json do CDN, alinha os blocos às
 * palavras (timestamps reais) e permite mover as quebras de bloco:
 * Enter quebra no caret, Backspace/Delete unem blocos. Os tempos de cada
 * bloco são SEMPRE derivados dos timestamps reais das palavras, então a
 * sincronização com a fala nunca é alterada.
 *
 * Props: { open, onOpenChange, item, expiryHours } — item = objeto de áudio do
 * queue (jobId, fileName, srtTempo, audioUrl, duration, createdAt). Sem
 * audioUrl o player some e o resto funciona igual (caso da Transcrição).
 * expiryHours = retenção dos arquivos no R2 (24h no TTS, 7 dias na Transcrição).
 */
const SrtEditorModal = ({ open, onOpenChange, item, expiryHours = 24 }) => {
  const { t } = useTranslation();

  const [phase, setPhase] = useState('loading'); // loading | ready | error
  const [errorKey, setErrorKey] = useState(null); // expired | srt | no_words | align
  const [words, setWords] = useState([]); // imutável após load
  const [displayWords, setDisplayWords] = useState([]);
  const [breaks, setBreaks] = useState([]);
  const [initialBreaks, setInitialBreaks] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(0);
  const [caret, setCaret] = useState(0); // índice global: "antes da palavra caret"
  const [downloaded, setDownloaded] = useState(true);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [smartMaxSec, setSmartMaxSec] = useState(8); // teto (s) da quebra por frases
  // Mínimo (s) por bloco na quebra por frases. 0 = sem mínimo (comportamento
  // original); subir evita bloco com uma palavra solta.
  const [smartMinSec, setSmartMinSec] = useState(0);
  // Faixas por grupo: primeiros N blocos com min-max próprios; demais usam
  // os campos principais (smartMinSec/smartMaxSec).
  const [zonesOn, setZonesOn] = useState(false);
  const [zoneCount, setZoneCount] = useState(5);
  const [zoneMinSec, setZoneMinSec] = useState(0);
  const [zoneMaxSec, setZoneMaxSec] = useState(8);
  // Painel "Como usar" — abre sozinho na primeira vez (localStorage)
  const [helpOpen, setHelpOpen] = useState(false);

  // Player
  const audioRef = useRef(null);
  const editorRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentMs, setCurrentMs] = useState(0);

  const blocks = useMemo(
    () => blocksFromBreaks(words, breaks, displayWords),
    [words, breaks, displayWords]
  );

  const dirty = useMemo(
    () => JSON.stringify(breaks) !== JSON.stringify(initialBreaks),
    [breaks, initialBreaks]
  );

  // ── Fetch em 2 camadas: CDN direto → proxy do backend (CORS) ──
  const fetchText = useCallback(async (url) => {
    try {
      const r = await fetch(url);
      if (r.ok) {
        const text = await r.text();
        if (text) return text;
      }
    } catch (e) { /* tenta proxy */ }
    const token = localStorage.getItem('token');
    const r = await fetch(`${BACKEND_URL}/api/proxy-download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ url }),
    });
    if (!r.ok) throw new Error('fetch');
    const text = await r.text();
    if (!text) throw new Error('fetch');
    return text;
  }, []);

  // ── Load ao abrir ──
  useEffect(() => {
    if (!open || !item) return undefined;
    let cancelled = false;
    const fail = (key) => {
      if (!cancelled) {
        setErrorKey(key);
        setPhase('error');
      }
    };
    const load = async () => {
      setPhase('loading');
      setErrorKey(null);
      setCaret(0);
      setCurrentMs(0);
      setDownloaded(true);

      if (item.createdAt) {
        const hoursPassed = (Date.now() - new Date(item.createdAt).getTime()) / 3600000;
        if (hoursPassed >= expiryHours) { fail('expired'); return; }
      }
      const srtUrl = item.srtTempo || item.srtVeo;
      if (!srtUrl) { fail('srt'); return; }

      let srtText;
      try {
        srtText = await fetchText(srtUrl);
      } catch (e) { fail('srt'); return; }

      let wordsJson;
      try {
        wordsJson = JSON.parse(await fetchText(wordsUrlFromSrtUrl(srtUrl)));
      } catch (e) { fail('no_words'); return; }

      try {
        const parsedBlocks = parseSrt(srtText);
        const normWords = normalizeWords(wordsJson);
        const { breaks: alignedBreaks, displayWords: dws } = alignSrtToWords(parsedBlocks, normWords);
        if (cancelled) return;
        setWords(normWords);
        setDisplayWords(dws);
        setBreaks(alignedBreaks);
        setInitialBreaks(alignedBreaks);
        setHistory([alignedBreaks]);
        setHistoryIdx(0);
        // Teto sugerido p/ quebra por frases = maior bloco do SRT original
        const maxDur = Math.max(...parsedBlocks.map((blk) => blk.endMs - blk.startMs));
        const suggestedCap = Math.min(60, Math.max(1, Math.ceil(maxDur / 1000)));
        setSmartMaxSec(suggestedCap);
        setSmartMinSec(0);
        setZonesOn(false);
        setZoneMaxSec(suggestedCap);
        setZoneMinSec(0);
        // Primeira visita: mostra o guia "Como usar" aberto
        try {
          if (!localStorage.getItem('srt_editor_help_seen')) {
            setHelpOpen(true);
            localStorage.setItem('srt_editor_help_seen', '1');
          }
        } catch { /* ignore */ }
        setPhase('ready');
      } catch (e) {
        console.error('[SrtEditor] falha ao alinhar SRT x words_time:', e);
        fail('align');
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, item, fetchText, expiryHours]);

  // Pausa o áudio ao fechar
  useEffect(() => {
    if (!open && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [open]);

  // ── Operações de edição (só mexem em breaks) ──
  const applyBreaks = useCallback((next) => {
    setBreaks(next);
    setHistory((h) => [...h.slice(0, historyIdx + 1), next]);
    setHistoryIdx((i) => i + 1);
    setDownloaded(false);
  }, [historyIdx]);

  const splitAt = useCallback((i) => {
    if (i <= 0 || i >= words.length || breaks.includes(i)) return;
    applyBreaks([...breaks, i].sort((a, b) => a - b));
  }, [breaks, words.length, applyBreaks]);

  const mergeAt = useCallback((i) => {
    if (!breaks.includes(i)) return;
    applyBreaks(breaks.filter((b) => b !== i));
  }, [breaks, applyBreaks]);

  // Move a quebra k em +1/-1 palavra (setas ▲/▼ dos blocos). Se o movimento
  // esvazia um bloco (última palavra saiu), a quebra é removida e os blocos
  // vizinhos se fundem — os tempos seguem vindo das palavras, sync intacto.
  const moveBreak = useCallback((k, delta) => {
    const bounds = [0, ...breaks, words.length];
    const next = breaks[k] + delta;
    if (next < bounds[k] || next > bounds[k + 2]) return;
    if (next === bounds[k] || next === bounds[k + 2]) {
      applyBreaks(breaks.filter((_, idx) => idx !== k));
      return;
    }
    const updated = [...breaks];
    updated[k] = next;
    applyBreaks(updated);
  }, [breaks, words.length, applyBreaks]);

  const undo = useCallback(() => {
    if (historyIdx <= 0) return;
    setHistoryIdx((i) => i - 1);
    setBreaks(history[historyIdx - 1]);
    setDownloaded(false);
  }, [history, historyIdx]);

  const redo = useCallback(() => {
    if (historyIdx >= history.length - 1) return;
    setHistoryIdx((i) => i + 1);
    setBreaks(history[historyIdx + 1]);
    setDownloaded(false);
  }, [history, historyIdx]);

  const resetOriginal = useCallback(() => {
    applyBreaks(initialBreaks);
  }, [initialBreaks, applyBreaks]);

  // Quebra inteligente: blocos terminam em fim de frase, dentro de min-max.
  // Com faixas ligadas, os primeiros N blocos usam o min-max da faixa e os
  // demais usam os campos principais.
  const applySmartBreaks = useCallback(() => {
    const capSec = Math.min(60, Math.max(1, Number(smartMaxSec) || 8));
    const minSec = Math.min(capSec, Math.max(0, Number(smartMinSec) || 0));
    setSmartMaxSec(capSec);
    setSmartMinSec(minSec);

    let rules;
    if (zonesOn) {
      const count = Math.min(999, Math.max(1, Math.round(Number(zoneCount) || 1)));
      const zCap = Math.min(60, Math.max(1, Number(zoneMaxSec) || capSec));
      const zMin = Math.min(zCap, Math.max(0, Number(zoneMinSec) || 0));
      setZoneCount(count);
      setZoneMaxSec(zCap);
      setZoneMinSec(zMin);
      rules = [
        { count, minMs: zMin * 1000, maxMs: zCap * 1000 },
        { minMs: minSec * 1000, maxMs: capSec * 1000 },
      ];
    } else {
      rules = [{ minMs: minSec * 1000, maxMs: capSec * 1000 }];
    }

    const next = smartBreaksRanged(words, rules);
    applyBreaks(next);
    toast.success(t('audio.srt_editor_smart_done', { blocks: next.length + 1 }));
  }, [smartMaxSec, smartMinSec, zonesOn, zoneCount, zoneMinSec, zoneMaxSec, words, applyBreaks, t]);

  // ── Teclado ──
  const handleKeyDown = useCallback((e) => {
    if (phase !== 'ready') return;
    const key = e.key;
    if ((e.ctrlKey || e.metaKey) && key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) redo(); else undo();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && key.toLowerCase() === 'y') {
      e.preventDefault();
      redo();
      return;
    }
    if (key === 'ArrowLeft') { e.preventDefault(); setCaret((c) => Math.max(0, c - 1)); return; }
    if (key === 'ArrowRight') { e.preventDefault(); setCaret((c) => Math.min(words.length, c + 1)); return; }
    if (key === 'ArrowUp' || key === 'ArrowDown') {
      e.preventDefault();
      // pula para o início do bloco anterior/seguinte
      const bounds = [0, ...breaks, words.length];
      const idx = bounds.findIndex((b, i) => caret >= b && caret < (bounds[i + 1] ?? Infinity));
      const target = key === 'ArrowUp' ? bounds[Math.max(0, idx - 1)] : bounds[Math.min(bounds.length - 2, idx + 1)];
      setCaret(target);
      return;
    }
    if (key === ' ') { e.preventDefault(); return; } // espaço não rola o scroll
    if (key === 'Enter') { e.preventDefault(); splitAt(caret); return; }
    if (key === 'Backspace') {
      e.preventDefault();
      if (breaks.includes(caret)) mergeAt(caret);
      else setCaret((c) => Math.max(0, c - 1));
      return;
    }
    if (key === 'Delete') {
      e.preventDefault();
      if (breaks.includes(caret)) mergeAt(caret);
      else setCaret((c) => Math.min(words.length, c + 1));
    }
  }, [phase, caret, breaks, words.length, splitAt, mergeAt, undo, redo]);

  // ── Player ──
  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) audio.pause();
    else audio.play().catch(() => {});
  }, [isPlaying]);

  const handleWordClick = useCallback((i) => {
    setCaret(i);
    if (editorRef.current) editorRef.current.focus();
    const audio = audioRef.current;
    if (audio && words[i]) {
      audio.currentTime = words[i].startMs / 1000;
      setCurrentMs(words[i].startMs);
    }
  }, [words]);

  // Palavra corrente durante o playback (busca binária)
  const currentWordIdx = useMemo(() => {
    if (!isPlaying || words.length === 0) return -1;
    let lo = 0;
    let hi = words.length - 1;
    let ans = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (words[mid].startMs <= currentMs) { ans = mid; lo = mid + 1; }
      else hi = mid - 1;
    }
    if (ans >= 0 && currentMs > words[ans].endMs + 400) return -1;
    return ans;
  }, [isPlaying, currentMs, words]);

  // Acompanha o tempo do áudio por requestAnimationFrame: o timeupdate nativo
  // dispara só a cada ~250ms e pulava palavras curtas ("o", "que") no karaokê.
  // Atualiza o estado apenas ao trocar de palavra ou a cada ~120ms (progresso).
  useEffect(() => {
    if (!isPlaying) return undefined;
    let raf;
    let lastIdx = -2;
    let lastTs = 0;
    const findIdx = (ms) => {
      let lo = 0; let hi = words.length - 1; let ans = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (words[mid].startMs <= ms) { ans = mid; lo = mid + 1; }
        else hi = mid - 1;
      }
      return ans;
    };
    const tick = (ts) => {
      const audio = audioRef.current;
      if (audio) {
        const ms = Math.round(audio.currentTime * 1000);
        const idx = findIdx(ms);
        if (idx !== lastIdx || ts - lastTs > 120) {
          lastIdx = idx;
          lastTs = ts;
          setCurrentMs(ms);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying, words]);

  // ── Download ──
  const handleDownload = useCallback(() => {
    const content = buildSrt(blocks);
    if (!content) return;
    const hasUserName = item?.fileName && !/^darkplanner_audio_/i.test(item.fileName);
    const safeName = hasUserName
      ? `${item.fileName.replace(/\.(mp3|wav|m4a|mp4|ogg|webm|mpga|mpeg|flac)$/i, '')}_srtsynctempo.srt`
      : 'Darkplanner_srtsynctempo.srt';
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = safeName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloaded(true);
    toast.success(t('audio.srt_downloaded'));
  }, [blocks, item, t]);

  // ── Fechar com edições não baixadas → ConfirmDialog padrão do site ──
  const handleOpenChange = useCallback((next) => {
    if (!next && dirty && !downloaded) {
      setConfirmCloseOpen(true);
      return;
    }
    onOpenChange(next);
  }, [dirty, downloaded, onOpenChange]);

  if (!item) return null;

  const totalMs = words.length > 0 ? words[words.length - 1].endMs : 0;
  const fmtSec = (ms) => `${(ms / 1000).toFixed(2).replace('.', ',')}s`;

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className=""
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        style={{
          background: 'var(--dp-bg-card)',
          border: '1px solid var(--dp-border-subtle)',
          borderRadius: '16px',
          padding: 0,
          maxWidth: '860px',
          width: 'calc(100% - 32px)',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03)',
          fontFamily: "var(--dp-font, 'Inter', sans-serif)",
        }}
      >
        <DialogTitle className="sr-only">{t('audio.srt_editor_title')}: {item.fileName}</DialogTitle>

        {/* ── Header ── */}
        {/* paddingRight maior: reserva o canto do X de fechar do Dialog
            (absolute right-4 top-4) pra nada ficar por baixo dele */}
        <div style={{
          padding: '18px 56px 14px 24px',
          borderBottom: '1px solid var(--dp-border-subtle)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexShrink: 0,
        }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '10px',
            background: 'rgba(13, 174, 255, 0.1)',
            border: '1px solid rgba(13, 174, 255, 0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Pencil size={17} style={{ color: '#0DAEFF' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '15px', fontWeight: 600, color: 'var(--dp-text-primary)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {t('audio.srt_editor_title')} · {item.fileName || 'Áudio'}
            </div>
            <div style={{ fontSize: '11.5px', color: 'var(--dp-text-tertiary)', marginTop: '2px' }}>
              {t('audio.srt_editor_hint')}
            </div>
          </div>
          {phase === 'ready' && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              padding: '4px 10px', borderRadius: '6px',
              background: 'var(--dp-bg-secondary)', border: '1px solid var(--dp-border-subtle)',
              fontSize: '11px', color: 'var(--dp-text-secondary)', whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              <Type size={12} style={{ color: 'var(--dp-text-tertiary)' }} />
              {t('audio.srt_editor_words_count', { blocks: blocks.length, words: words.length })}
            </span>
          )}
          {phase === 'ready' && (
            <button
              type="button"
              onClick={() => setHelpOpen((v) => !v)}
              title={t('audio.srt_editor_help_toggle')}
              style={{
                width: '28px', height: '28px', borderRadius: '8px',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: helpOpen ? 'rgba(13,174,255,0.12)' : 'transparent',
                border: helpOpen ? '1px solid rgba(13,174,255,0.3)' : '1px solid var(--dp-border-medium)',
                color: helpOpen ? '#0DAEFF' : 'var(--dp-text-secondary)',
                cursor: 'pointer', padding: 0, flexShrink: 0, transition: 'all 0.15s',
              }}
            >
              <HelpCircle size={15} />
            </button>
          )}
        </div>

        {/* ── Guia "Como usar" (colapsável; abre sozinho na 1ª visita) ── */}
        {phase === 'ready' && helpOpen && (
          <div style={{
            padding: '12px 24px 14px',
            borderBottom: '1px solid var(--dp-border-subtle)',
            background: 'rgba(13,174,255,0.03)',
            flexShrink: 0,
            maxHeight: '38vh',
            overflowY: 'auto',
          }} className="ag-scrollbar">
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
              gap: '10px 18px',
            }}>
              {[
                { icon: <CornerDownLeft size={13} />, title: t('audio.srt_editor_help_split_t'), desc: t('audio.srt_editor_help_split_d') },
                { icon: <ChevronsUp size={13} />, title: t('audio.srt_editor_help_merge_t'), desc: t('audio.srt_editor_help_merge_d') },
                { icon: <ChevronUp size={13} />, title: t('audio.srt_editor_help_move_t'), desc: t('audio.srt_editor_help_move_d') },
                { icon: <Wand2 size={13} />, title: t('audio.srt_editor_help_smart_t'), desc: t('audio.srt_editor_help_smart_d') },
                { icon: <SlidersHorizontal size={13} />, title: t('audio.srt_editor_help_zones_t'), desc: t('audio.srt_editor_help_zones_d') },
                { icon: <ShieldCheck size={13} />, title: t('audio.srt_editor_help_sync_t'), desc: t('audio.srt_editor_help_sync_d') },
              ].map((row, i) => (
                <div key={i} style={{ display: 'flex', gap: '9px', alignItems: 'flex-start' }}>
                  <span style={{
                    width: '24px', height: '24px', borderRadius: '7px', flexShrink: 0,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(13,174,255,0.1)', border: '1px solid rgba(13,174,255,0.18)',
                    color: '#0DAEFF', marginTop: '1px',
                  }}>
                    {row.icon}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--dp-text-primary)', marginBottom: '1px' }}>
                      {row.title}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--dp-text-secondary)', lineHeight: 1.45 }}>
                      {row.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{
              marginTop: '10px', fontSize: '10.5px', color: 'var(--dp-text-tertiary)',
              display: 'flex', alignItems: 'center', gap: '5px',
            }}>
              <Undo2 size={11} />
              {t('audio.srt_editor_help_undo_tip')}
            </div>
          </div>
        )}

        {/* ── Player ── */}
        {phase === 'ready' && item.audioUrl && (
          <div style={{
            padding: '10px 24px',
            borderBottom: '1px solid var(--dp-border-subtle)',
            display: 'flex', alignItems: 'center', gap: '12px',
            flexShrink: 0,
          }}>
            <button
              onClick={togglePlay}
              style={{
                width: '32px', height: '32px', borderRadius: '50%',
                background: 'rgba(13, 174, 255, 0.12)',
                border: '1px solid rgba(13, 174, 255, 0.25)',
                color: '#0DAEFF', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'all 0.2s',
              }}
            >
              {isPlaying ? <Pause size={14} /> : <Play size={14} style={{ marginLeft: '2px' }} />}
            </button>
            <span style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
              {formatSrtTime(currentMs).slice(3, 8)} / {formatSrtTime(totalMs).slice(3, 8)}
            </span>
            <div style={{ flex: 1, height: '4px', borderRadius: '2px', background: 'var(--dp-border-subtle)', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: totalMs > 0 ? `${Math.min(100, (currentMs / totalMs) * 100)}%` : '0%',
                background: 'linear-gradient(90deg, #0DAEFF, #3B82F6)',
                transition: 'width 0.2s linear',
              }} />
            </div>
            <audio
              ref={audioRef}
              src={item.audioUrl}
              preload="metadata"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              onTimeUpdate={(e) => setCurrentMs(Math.round(e.currentTarget.currentTime * 1000))}
            />
          </div>
        )}

        {/* ── Corpo ── */}
        {phase === 'loading' && (
          <div style={{ padding: '60px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <Loader2 size={22} style={{ color: '#0DAEFF', animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: '13px', color: 'var(--dp-text-secondary)' }}>{t('audio.srt_editor_loading')}</span>
          </div>
        )}

        {phase === 'error' && (
          <div style={{ padding: '48px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', textAlign: 'center' }}>
            <AlertTriangle size={26} style={{ color: '#F59E0B' }} />
            <span style={{ fontSize: '13px', color: 'var(--dp-text-primary)', maxWidth: '420px', lineHeight: 1.6 }}>
              {errorKey === 'expired' && t('audio.srt_expired')}
              {errorKey === 'srt' && t('audio.srt_unavailable')}
              {errorKey === 'no_words' && t('audio.srt_editor_no_words')}
              {errorKey === 'align' && t('audio.srt_editor_align_error')}
            </span>
          </div>
        )}

        {phase === 'ready' && (
          <div
            ref={editorRef}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            className="ag-scrollbar"
            style={{
              padding: '16px 24px',
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
              outline: 'none',
            }}
          >
            {blocks.map((blk, bi) => {
              const gapMs = bi < blocks.length - 1 ? blocks[bi + 1].startMs - blk.endMs : 0;
              return (
                <React.Fragment key={blk.wordStart}>
                  <div style={{
                    background: 'var(--dp-bg-secondary)',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '10px',
                    padding: '10px 14px 12px',
                    marginBottom: gapMs > 0 ? 0 : '10px',
                  }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      marginBottom: '8px', fontSize: '10.5px', color: 'var(--dp-text-tertiary)',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      <span style={{
                        fontWeight: 700, color: '#0DAEFF',
                        background: 'rgba(13,174,255,0.08)',
                        border: '1px solid rgba(13,174,255,0.15)',
                        borderRadius: '5px', padding: '1px 7px',
                      }}>
                        {bi + 1}
                      </span>
                      <span>{formatSrtTime(blk.startMs)} → {formatSrtTime(blk.endMs)}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                        <Clock size={10} /> {fmtSec(blk.endMs - blk.startMs)}
                      </span>
                      <span style={{ flex: 1 }} />
                      {bi > 0 && (
                        <BreakArrow
                          title={t('audio.srt_editor_merge_up')}
                          onClick={() => mergeAt(blk.wordStart)}
                          icon={<ChevronsUp size={13} />}
                        />
                      )}
                      {bi > 0 && (
                        <BreakArrow
                          title={t('audio.srt_editor_pull_up')}
                          onClick={() => moveBreak(bi - 1, 1)}
                          icon={<ChevronUp size={13} />}
                        />
                      )}
                      {bi < blocks.length - 1 && (
                        <BreakArrow
                          title={t('audio.srt_editor_push_down')}
                          onClick={() => moveBreak(bi, -1)}
                          icon={<ChevronDown size={13} />}
                        />
                      )}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', rowGap: '6px', lineHeight: 1.5 }}>
                      {Array.from({ length: blk.wordEnd - blk.wordStart }, (_, k) => {
                        const i = blk.wordStart + k;
                        return (
                          <React.Fragment key={i}>
                            {caret === i && <CaretBar />}
                            <span
                              onClick={() => handleWordClick(i)}
                              style={{
                                fontSize: '13.5px',
                                color: currentWordIdx === i ? '#FFFFFF' : 'var(--dp-text-primary)',
                                background: currentWordIdx === i ? '#0DAEFF' : 'transparent',
                                borderRadius: '4px',
                                padding: '1px 3px',
                                margin: '0 1px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                transition: 'background 0.15s, color 0.15s',
                              }}
                              onMouseEnter={(e) => { if (currentWordIdx !== i) e.currentTarget.style.background = 'rgba(13,174,255,0.12)'; }}
                              onMouseLeave={(e) => { if (currentWordIdx !== i) e.currentTarget.style.background = 'transparent'; }}
                            >
                              {displayWords[i]}
                            </span>
                          </React.Fragment>
                        );
                      })}
                      {bi === blocks.length - 1 && caret === words.length && <CaretBar />}
                    </div>
                  </div>
                  {gapMs > 0 && (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '3px 0 3px', margin: '2px 0 8px',
                    }}>
                      <span style={{
                        fontSize: '10px', color: 'var(--dp-text-tertiary)',
                        background: 'var(--dp-bg-secondary)',
                        border: '1px dashed var(--dp-border-medium)',
                        borderRadius: '999px', padding: '2px 10px',
                      }}>
                        + {t('audio.srt_editor_gap', { s: (gapMs / 1000).toFixed(2).replace('.', ',') })}
                      </span>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* ── Footer ── */}
        {phase === 'ready' && (
          <div style={{
            borderTop: '1px solid var(--dp-border-subtle)',
            flexShrink: 0,
          }}>
            {/* Faixas por grupo: frase corrida "Os primeiros N blocos terão
                tempo de X a Y s · os demais usam o tempo por bloco abaixo" */}
            {zonesOn && (
              <div style={{
                padding: '9px 24px',
                borderBottom: '1px solid var(--dp-border-subtle)',
                display: 'flex', gap: '7px', alignItems: 'center', flexWrap: 'wrap',
                fontSize: '11.5px', color: 'var(--dp-text-primary)',
                background: 'rgba(13,174,255,0.04)',
              }}>
                <span style={{
                  width: '24px', height: '24px', borderRadius: '7px', flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: 'rgba(13,174,255,0.1)', border: '1px solid rgba(13,174,255,0.18)',
                  color: '#0DAEFF',
                }}>
                  <SlidersHorizontal size={13} />
                </span>
                <span>{t('audio.srt_editor_zone_first')}</span>
                <SecInput value={zoneCount} onChange={setZoneCount} min={1} max={999} width={48} title={t('audio.srt_editor_zone_count_tip')} />
                <span>{t('audio.srt_editor_zone_blocks')}</span>
                <SecInput value={zoneMinSec} onChange={setZoneMinSec} min={0} max={60} title={t('audio.srt_editor_smart_min_tip')} />
                <span>{t('audio.srt_editor_range_to')}</span>
                <SecInput value={zoneMaxSec} onChange={setZoneMaxSec} min={1} max={60} title={t('audio.srt_editor_smart_max_tip')} />
                <span>s</span>
                <span style={{ color: 'var(--dp-text-tertiary)', whiteSpace: 'nowrap' }}>· {t('audio.srt_editor_zone_rest')}</span>
              </div>
            )}

            <div style={{
              padding: '10px 24px 14px',
              display: 'flex', gap: '8px', alignItems: 'center',
            }}>
              {/* Quebra inteligente por frases (min-max) */}
              <FooterButton onClick={applySmartBreaks} icon={<Wand2 size={14} />} label={t('audio.srt_editor_smart_btn')} primary title={t('audio.srt_editor_smart_tip')} />
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                fontSize: '11.5px', color: 'var(--dp-text-secondary)', whiteSpace: 'nowrap',
              }}>
                {t('audio.srt_editor_smart_range_label')}
                <SecInput value={smartMinSec} onChange={setSmartMinSec} min={0} max={60} title={t('audio.srt_editor_smart_min_tip')} />
                {t('audio.srt_editor_range_to')}
                <SecInput value={smartMaxSec} onChange={setSmartMaxSec} min={1} max={60} title={t('audio.srt_editor_smart_max_tip')} />
                s
              </label>
              <button
                type="button"
                onClick={() => setZonesOn((v) => !v)}
                title={t('audio.srt_editor_zone_toggle')}
                style={{
                  height: '29px', padding: '0 10px', borderRadius: '8px',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                  background: zonesOn ? 'rgba(13,174,255,0.1)' : 'transparent',
                  border: zonesOn ? '1px solid rgba(13,174,255,0.25)' : '1px solid var(--dp-border-medium)',
                  color: zonesOn ? '#0DAEFF' : 'var(--dp-text-secondary)',
                  fontSize: '11.5px', fontWeight: 500,
                  fontFamily: "var(--dp-font, 'Inter', sans-serif)",
                  cursor: 'pointer', transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => { if (!zonesOn) e.currentTarget.style.background = 'var(--dp-bg-secondary)'; }}
                onMouseLeave={(e) => { if (!zonesOn) e.currentTarget.style.background = 'transparent'; }}
              >
                <SlidersHorizontal size={13} />
                {t('audio.srt_editor_zone_btn')}
              </button>
              <div style={{ width: '1px', height: '20px', background: 'var(--dp-border-subtle)', margin: '0 4px' }} />
              <FooterButton onClick={undo} disabled={historyIdx <= 0} icon={<Undo2 size={14} />} label={t('audio.srt_editor_undo')} iconOnly />
              <FooterButton onClick={redo} disabled={historyIdx >= history.length - 1} icon={<Redo2 size={14} />} label={t('audio.srt_editor_redo')} iconOnly />
              <FooterButton onClick={resetOriginal} disabled={!dirty} icon={<RotateCcw size={14} />} label={t('audio.srt_editor_reset')} iconOnly />
              <div style={{ flex: 1 }} />
              <FooterButton onClick={handleDownload} icon={<Download size={14} />} label={t('audio.srt_editor_download')} primary />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Confirmação de fechar com edições não baixadas (padrão do site).
        Wrapper com pointerEvents auto: o Dialog modal do Radix põe
        pointer-events none no body, o que bloquearia os cliques aqui. */}
    <div style={{ pointerEvents: 'auto' }}>
      <ConfirmDialog
        open={confirmCloseOpen}
        icon={<AlertTriangle />}
        title={t('audio.srt_editor_unsaved_title')}
        message={t('audio.srt_editor_unsaved')}
        confirmLabel={t('audio.srt_editor_unsaved_confirm')}
        cancelLabel={t('audio.srt_editor_unsaved_cancel')}
        danger
        onConfirm={() => { setConfirmCloseOpen(false); onOpenChange(false); }}
        onCancel={() => setConfirmCloseOpen(false)}
      />
    </div>
    </>
  );
};

/* ── Seta de mover palavra entre blocos vizinhos ── */
const BreakArrow = ({ title, onClick, disabled, icon }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    onClick={onClick}
    style={{
      width: '22px', height: '20px',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: 'transparent',
      border: '1px solid var(--dp-border-medium)',
      borderRadius: '5px',
      color: 'var(--dp-text-secondary)',
      cursor: disabled ? 'default' : 'pointer',
      padding: 0,
      opacity: disabled ? 0.4 : 1,
      transition: 'all 0.15s',
    }}
    onMouseEnter={(e) => {
      if (!disabled) {
        e.currentTarget.style.background = 'rgba(13,174,255,0.12)';
        e.currentTarget.style.color = '#0DAEFF';
        e.currentTarget.style.borderColor = 'rgba(13,174,255,0.3)';
      }
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'transparent';
      e.currentTarget.style.color = 'var(--dp-text-secondary)';
      e.currentTarget.style.borderColor = 'var(--dp-border-medium)';
    }}
  >
    {icon}
  </button>
);

/* ── Input numérico compacto (segundos / contagem) do footer ── */
const SecInput = ({ value, onChange, min, max, width = 44, title }) => (
  <input
    type="number"
    min={min}
    max={max}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    title={title}
    style={{
      width: `${width}px`, height: '29px', padding: '0 6px', borderRadius: '8px',
      background: 'var(--dp-bg-secondary)', border: '1px solid var(--dp-border-medium)',
      color: 'var(--dp-text-primary)', fontSize: '11.5px', textAlign: 'center',
      fontFamily: 'var(--dp-font)', outline: 'none', transition: 'border-color 0.2s',
    }}
    onFocus={(e) => { e.target.style.borderColor = 'var(--dp-blue-500, #0DAEFF)'; }}
    onBlur={(e) => { e.target.style.borderColor = 'var(--dp-border-medium)'; }}
  />
);

/* ── Caret (barra azul piscante entre palavras) ── */
const CaretBar = () => (
  <span
    style={{
      display: 'inline-block',
      width: '2px',
      height: '17px',
      background: '#0DAEFF',
      borderRadius: '1px',
      margin: '0 1px',
      verticalAlign: 'middle',
      boxShadow: '0 0 6px rgba(13,174,255,0.7)',
      animation: 'srtCaretBlink 1s steps(2, start) infinite',
    }}
  >
    <style>{'@keyframes srtCaretBlink { 50% { opacity: 0; } }'}</style>
  </span>
);

/* ── Botão do footer ──
   primary = degradê azul padrão DarkPlanner (mesmo dos CTAs/pills);
   iconOnly = só o ícone, com o label vivendo no tooltip nativo. */
const FooterButton = ({ onClick, icon, label, primary, disabled, title, iconOnly }) => {
  const [hovered, setHovered] = useState(false);
  const baseStyle = primary
    ? {
        background: hovered && !disabled
          ? 'linear-gradient(135deg, #1FB8FF 0%, #4F8FF8 100%)'
          : 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
        border: '1px solid transparent',
        color: '#FFFFFF',
      }
    : {
        background: hovered && !disabled ? 'var(--dp-bg-secondary)' : 'transparent',
        border: '1px solid var(--dp-border-medium)',
        color: disabled ? 'var(--dp-text-tertiary)' : hovered ? 'var(--dp-text-primary)' : 'var(--dp-text-secondary)',
      };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title || (iconOnly ? label : undefined)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...baseStyle,
        padding: iconOnly ? '6px 8px' : '6px 12px',
        height: '29px',
        borderRadius: '8px',
        fontSize: '11.5px',
        fontWeight: primary ? 600 : 500,
        cursor: disabled ? 'default' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '5px',
        transition: 'all 0.2s',
        fontFamily: "var(--dp-font, 'Inter', sans-serif)",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {icon}
      {!iconOnly && label}
    </button>
  );
};

export default SrtEditorModal;
